// Microsoft 365 in Studio: Outlook (mail and calendar), Teams, Planner, SharePoint and OneDrive.
// Works in VERA for desktop and on the web, with Microsoft sign-in. Each person connects their own account (delegated access),
// only after turning on "Email, calendar and Teams" in Privacy & consent. Creating anything asks first.
// The operations are one shared file (apps/desktop/src/m365ops.ts): VERA.exe runs it in its main process, the web app here.

import { useCallback, useEffect, useState } from "react";
import { desktop } from "./desktop";
import { auth, devMode } from "../auth/auth";
import { GRAPH_KINDS, GRAPH_SCOPES, GraphInputError, makeCall, OPS, type LinkInfo } from "../../../desktop/src/m365ops";
import { confirmInPage } from "../components/ConfirmHost";
import { settingsChanged } from "./history";
import { googleChosen, googleToken, setGooglePerson } from "./google";
import { GOOGLE_OPS, makeGoogleCall } from "./googleops";
import { L } from "./i18n";

export type M365Kind = "outlook" | "teams" | "planner" | "files";
export const M365: { id: M365Kind; name: string; nameSq: string; abbr: string; color: string; detail: string; detailSq: string }[] = [
  { id: "outlook", name: "Outlook mail and calendar", nameSq: "Posta dhe kalendari i Outlook", abbr: "Ol", color: "#2563c9", detail: "Your inbox and your day. VERA saves drafts for you to send, and books Teams meetings you approve.", detailSq: "Posta dhe dita jote. VERA ruan drafte që t'i dërgosh ti, dhe cakton takime në Teams që i miraton ti." },
  { id: "teams", name: "Microsoft Teams", nameSq: "Microsoft Teams", abbr: "Te", color: "#5b5fc7", detail: "Your recent chats and your teams, so nothing important slips past.", detailSq: "Bisedat e fundit dhe ekipet e tua, që asgjë e rëndësishme të mos të shpëtojë." },
  { id: "planner", name: "Microsoft Planner", nameSq: "Microsoft Planner", abbr: "Pl", color: "#31752f", detail: "Your tasks across plans. VERA creates tasks you approve, with owners and due dates.", detailSq: "Detyrat e tua në të gjitha planet. VERA krijon detyra që i miraton ti, me përgjegjës dhe afate." },
  { id: "files", name: "SharePoint & OneDrive", nameSq: "SharePoint dhe OneDrive", abbr: "Sp", color: "#0f7b6c", detail: "Your recent files and search across everything you can open. Read only.", detailSq: "Skedarët e fundit dhe kërkimi në gjithçka që mund të hapësh. Vetëm lexim." },
];

/** True when real Microsoft 365 is possible here: VERA for desktop or the web, with Microsoft sign-in, not the demo. */
export const m365Available = () => (desktop ? !devMode : auth.kind === "microsoft" && Boolean(auth.graphToken));
/** True in VERA for desktop with real sign-in. Azure, GitHub and folders on this computer are desktop only. */
export const desktopLive = () => Boolean(desktop) && !devMode;

// ---------- on the web: the same operations, run in this page ----------
/** Links in emails VERA read in this tab; only these can be opened (the same rule as VERA.exe). */
const webLinks = new Set<string>();
export const webLinkSeen = (url: string) => webLinks.has(url);

async function webStatus(): Promise<Record<string, boolean>> {
  const out: Record<string, boolean> = {};
  await Promise.all(GRAPH_KINDS.map(async (k) => { try { await auth.graphToken!([...GRAPH_SCOPES[k]], false); out[k] = true; } catch { out[k] = false; } }));
  return out;
}

async function webRun<T>(name: string, args: Record<string, unknown> = {}): Promise<T> {
  const op = Object.prototype.hasOwnProperty.call(OPS, name) ? OPS[name] : null;
  if (!op) throw new GraphInputError("That isn't something VERA can do yet.");
  let token: string;
  try { token = await auth.graphToken!([...GRAPH_SCOPES[op.kind]], false); }
  catch {
    // Sending needs Mail.Send, which Microsoft may ask for once, the first time the person presses Send.
    if (op.kind !== "mailsend") throw new GraphInputError("Please connect this in Connections first.");
    token = await auth.graphToken!([...GRAPH_SCOPES.mailsend], true);
  }
  const result = await op.run(args, makeCall(token), confirmInPage);
  if (name === "mail.read") for (const l of ((result as { links?: LinkInfo[] })?.links ?? [])) webLinks.add(l.url);
  return result as T;
}

export type CalEvent = { id: string; subject: string; start: string; end: string; webLink: string; teams: boolean; joinUrl: string | null; location: string; organizer: string; attendees: string[] };
export type Mail = { id: string; subject: string; from: { name: string; email: string }; received: string; preview: string; unread: boolean; webLink: string };
export type Chat = { id: string; topic: string; type: string; webUrl: string; last: { from: string; text: string; at: string } | null };
export type Task = { id: string; title: string; planId: string; plan: string; percent: number; due: string | null; priority: number; bucket?: string; assignees?: string[] };
export type Plan = { id: string; title: string; group: string; groupId?: string };
export type DriveFile = { id: string; name: string; webUrl: string; modified: string; size: number; where: string; by: string; summary?: string };

const key = (userId: string) => `vera-m365-${userId}`;
function chosen(userId: string): M365Kind[] {
  try { return JSON.parse(localStorage.getItem(key(userId)) ?? "[]") as M365Kind[]; } catch { return []; }
}
function setChosen(userId: string, kinds: M365Kind[]) {
  try { localStorage.setItem(key(userId), JSON.stringify(kinds)); } catch { /* ignore */ }
  settingsChanged();
  window.dispatchEvent(new Event("vera-m365"));
}

/** Which Microsoft 365 apps this person has connected (they chose to, and Microsoft gave access). */
export function useM365(userId: string, allowed: boolean) {
  setGooglePerson(userId);
  currentUser = userId;
  const [status, setStatus] = useState<Record<string, boolean>>({});
  const [, setTick] = useState(0);
  const [mine, setMine] = useState<M365Kind[]>(() => chosen(userId));
  const refresh = useCallback(async () => {
    setMine(chosen(userId));
    if (!m365Available()) return;
    try { setStatus(desktop ? await desktop.graph.status() : await webStatus()); } catch { setStatus({}); }
  }, [userId]);
  useEffect(() => {
    void refresh();
    const on = () => { setMine(chosen(userId)); setTick((n) => n + 1); };
    window.addEventListener("vera-m365", on);
    return () => window.removeEventListener("vera-m365", on);
  }, [refresh, userId]);
  const connected = (k: M365Kind) => allowed && m365Available() && mine.includes(k) && Boolean(status[k]);
  outlookUsable = connected("outlook");
  /** Email and calendar are connected: Outlook, or Gmail and Google Calendar. */
  const mail = () => connected("outlook") || (allowed && googleChosen());
  async function connect(k: M365Kind) {
    if (desktop) await desktop.graph.connect(k);
    // On the web, Outlook also asks for sending now: Microsoft's window can only open right after a click.
    else await auth.graphToken!([...new Set([...GRAPH_SCOPES[k], ...(k === "outlook" ? GRAPH_SCOPES.mailsend : [])])], true);
    setChosen(userId, [...new Set([...chosen(userId), k])]);
    await refresh();
  }
  function disconnect(k: M365Kind) { setChosen(userId, chosen(userId).filter((x) => x !== k)); }
  return { connected, mail, connect, disconnect, refresh, ready: Object.keys(status).length > 0 };
}

// ---------- Gmail and Google Calendar behind the same doorway ----------
let outlookUsable = false;
let currentUser = "";
const provKey = () => `vera-mailprov-${currentUser}`;
/** Email and calendar come from Google when Google is connected and Outlook isn't (or the person chose Gmail). */
export function mailFromGoogle(): boolean {
  if (!googleChosen()) return false;
  if (!outlookUsable) return true;
  try { return localStorage.getItem(provKey()) === "google"; } catch { return false; }
}
export function setMailProvider(p: "google" | "outlook") { try { localStorage.setItem(provKey(), p); } catch { /* ignore */ } settingsChanged(); window.dispatchEvent(new Event("vera-m365")); }

async function googleRun<T>(name: string, args: Record<string, unknown> = {}): Promise<T> {
  const op = GOOGLE_OPS[name];
  const result = await op(args, makeGoogleCall(googleToken()), confirmInPage);
  if (name === "mail.read") for (const l of ((result as { links?: LinkInfo[] })?.links ?? [])) webLinks.add(l.url);
  return result as T;
}

export const graph = <T,>(op: string, args?: Record<string, unknown>) =>
  Object.prototype.hasOwnProperty.call(GOOGLE_OPS, op) && mailFromGoogle() ? googleRun<T>(op, args)
    : desktop ? desktop.graph.run<T>(op, args) : webRun<T>(op, args);

/** Load something from Microsoft 365 when connected. */
export function useGraph<T>(op: string, on: boolean, args?: Record<string, unknown>) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const argKey = JSON.stringify(args ?? {});
  const load = useCallback(async () => {
    if (!on) return;
    setLoading(true); setError("");
    try { setData(await graph<T>(op, JSON.parse(argKey))); } catch (e) { setError(e instanceof Error ? e.message.replace(/^Error invoking remote method '[^']+': (Error: )?/, "") : "Couldn't load this."); }
    finally { setLoading(false); }
  }, [op, on, argKey]);
  useEffect(() => { void load(); }, [load]);
  return { data, error, loading, reload: load };
}

export const cleanError = (e: unknown) => (e instanceof Error ? e.message.replace(/^Error invoking remote method '[^']+': (Error: )?/, "") : L("Something went wrong.", "Diçka shkoi keq."));
export const timeOf = (iso: string) => new Date(iso.endsWith("Z") || /[+-]\d\d:\d\d$/.test(iso) ? iso : iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
export const openLink = (url?: string | null) => { if (url && url.startsWith("https://")) window.open(url, "_blank", "noopener"); };
